import { apiClient } from '@/config/api';

export interface AdminUser {
  uid: string;
  email: string;
  displayName?: string;
  admin: boolean;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: AdminUser;
  message: string;
}

export interface ResumeArchiveItem {
  id: string;
  name: string;
  email: string;
  fileName: string;
  uploadedAt: Date;
  uploadDate: {
    year: string;
    month: string;
    day: string;
  };
  analysisCount: number;
  sessionId: string;
  resumeStoragePath: string;
}

export interface AIUsageLog {
  id: string;
  feature: string;
  sessionId: string;
  timestamp: Date;
  inputLength: number;
  outputLength: number;
}

export interface Analytics {
  totalResumes: number;
  totalAIUsage: number;
  recentResumes: number;
  recentAIUsage: number;
  featureBreakdown: Record<string, number>;
}

class AdminService {
  private token: string | null = null;

  // Set admin token
  setToken(token: string) {
    this.token = token;
    localStorage.setItem('adminToken', token);
  }

  // Get admin token
  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('adminToken');
    }
    return this.token;
  }

  // Clear admin token
  clearToken() {
    this.token = null;
    localStorage.removeItem('adminToken');
  }

  // Get auth headers
  private getAuthHeaders() {
    const token = this.getToken();
    if (!token) {
      throw new Error('No admin token available');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Admin login
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post('/api/admin-auth/login', {
        email,
        password
      });

      if (response.data.success) {
        this.setToken(response.data.token);
      }

      return response.data;
    } catch (error: any) {
      // Handle rate limiting specifically
      if (error.response?.status === 429) {
        throw new Error('Too many requests from this IP, please try again later.');
      }
      
      // Handle other errors
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  }

  // Verify admin token
  async verifyToken(): Promise<{ success: boolean; user: AdminUser }> {
    try {
      const response = await apiClient.get('/api/admin-auth/verify', {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      this.clearToken();
      throw new Error('Token verification failed');
    }
  }

  // Admin logout
  async logout(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/api/admin-auth/logout', {}, {
        headers: this.getAuthHeaders()
      });
      this.clearToken();
      return response.data;
    } catch (error: any) {
      this.clearToken();
      return { success: true, message: 'Logged out' };
    }
  }

  // Get resume archive
  async getResumeArchive(params: {
    page?: number;
    limit?: number;
    search?: string;
    dateFilter?: string;
  } = {}): Promise<{
    success: boolean;
    resumes: ResumeArchiveItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  }> {
    try {
      const response = await apiClient.get('/api/admin-data/resume-archive', {
        headers: this.getAuthHeaders(),
        params
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch resume archive');
    }
  }

  // Get resume details
  async getResumeDetails(id: string): Promise<{
    success: boolean;
    resume: any;
  }> {
    try {
      const response = await apiClient.get(`/api/admin-data/resume-details/${id}`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch resume details');
    }
  }

  // Download resume file
  async downloadResumeFile(id: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`/api/admin-data/download-file/${id}`, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to download file');
    }
  }

  // Get AI usage logs
  async getAIUsageLogs(params: {
    page?: number;
    limit?: number;
    feature?: string;
    dateFilter?: string;
  } = {}): Promise<{
    success: boolean;
    logs: AIUsageLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      hasMore: boolean;
    };
  }> {
    try {
      const response = await apiClient.get('/api/admin-data/ai-usage-logs', {
        headers: this.getAuthHeaders(),
        params
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch AI usage logs');
    }
  }

  // Get analytics
  async getAnalytics(): Promise<{
    success: boolean;
    analytics: Analytics;
  }> {
    try {
      const response = await apiClient.get('/api/admin-data/analytics', {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch analytics');
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }
}

export const adminService = new AdminService();
